import {
  faCircleCheck,
  faCircleExclamation,
  faHexagonExclamation,
  faCircleInfo,
  faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import { Button } from '@rocketlane/ui';
import { notification } from 'antd';
import {
  Divider,
  CloseButton,
  ToastIcon,
  ToastDescription,
  ToastWrapper,
  VariantIcon,
  CloseIcon,
} from './toast.styled';
import { ToastProps, ToastNotificationProps, ToastType } from './types';
import { ToastUI } from '../../styles/colors';

// ✅ Toast Icons
const Icons = {
  SUCCESS: (
    <VariantIcon variant={ToastType.Success} icon={faCircleCheck} size={32} />
  ),
  ERROR: (
    <VariantIcon
      variant={ToastType.Error}
      icon={faHexagonExclamation}
      size={32}
    />
  ),
  WARNING: (
    <VariantIcon
      variant={ToastType.Warning}
      icon={faCircleExclamation}
      size={32}
    />
  ),
  INFO: <VariantIcon variant={ToastType.Info} icon={faCircleInfo} size={32} />,
};

const DEFAULT_TOAST_STYLE = {
  backgroundColor: ToastUI.backgroundColor,
  color: ToastUI.textColor,
  borderRadius: ToastUI.borderRadius,
  padding: ToastUI.padding,
  width: 'auto',
  minWidth: ToastUI.minWidth,
  zIndex: ToastUI.zIndex,
};

// ✅ Reusable Toast Content Component
const NotificationContent = ({
  icon,
  description,
  action,
  notificationKey,
}: ToastNotificationProps) => (
  <ToastWrapper>
    <ToastIcon>{icon}</ToastIcon>
    <ToastDescription>{description}</ToastDescription>
    {action?.label && (
      <Button variant="link" size="small" onClick={action?.onClick}>
        {action.label}
      </Button>
    )}
    <Divider />
    <CloseButton
      role="button"
      aria-label="Close notification"
      onClick={() => notificationKey && notification.close(notificationKey)}
    >
      <CloseIcon icon={faXmark} size={16} />
    </CloseButton>
  </ToastWrapper>
);

// ✅ showToast Function
export const showToast = ({
  toastContainer,
  duration = 3, // Default to 3 seconds
  content,
  variant = ToastType.Info,
  placement = 'bottomLeft',
  key = `toast_${Date.now()}`, // Generate unique key
  action,
  onClose,
  style,
}: ToastProps): void => {
  const config = {
    placement,
    duration,
    getContainer: () => {
      return toastContainer?.current || document.body;
    },
    closeIcon: null,
    style: { ...DEFAULT_TOAST_STYLE, ...style },
  };

  /* we displaying customizeed toast content we don't need message here because we have content */
  notification.open({
    key,
    message: null,
    description: (
      <NotificationContent
        icon={Icons[variant]}
        description={content || ''}
        action={action}
        onClose={onClose}
        notificationKey={key}
      />
    ),
    ...config,
  });
};
